/**
 * Menu 2 · Submenu B: Trainingen Overzicht — Roster & Attendance Matrix.
 * Live vulgraden, Bulk-Groepschat, presentielijst met directe naam- en e-mailvrijgave.
 * Chat-Hub-dieplinks: [Start Chat] per cursist → Menu 5 Tab A; [Stuur Groepsbericht] → Bulk-Broadcast
 * in de losse 1-op-1 threads van alle cursisten (optioneel één sessie/slot).
 * TRAINER ziet alleen eigen klassen en kan niet bewerken.
 */
import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { BookOpen, Users, MessagesSquare, ClipboardList, Edit, X, Mail, Phone, Plus, MessageCircle, Megaphone, CheckCircle2, Award, Info, Loader2 } from 'lucide-react';
import { getRoster, getPresentie, bevestigPresentie, startGroepschat, stuurBroadcast, RosterTraining, PresentieSessie, Deelnemer } from '../services/opleiderCockpitApi';
import { useOpleiderRol } from '../components/opleiderCockpit/useOpleiderRol';

const Vulgraad: React.FC<{ t: RosterTraining }> = ({ t }) => {
  const { t: tr } = useTranslation('pages');
  if (t.vulgraad === null) return <span className="text-xs text-gray-400 whitespace-nowrap">{tr('opleiderCockpit.roster.geenSessies')}</span>;
  const kleur = t.vulgraad >= 100 ? 'bg-red-500' : t.vulgraad >= 80 ? 'bg-amber-400' : 'bg-emerald-500';
  return (
    <div className="min-w-[8rem]">
      <div className="flex justify-between text-xs text-gray-600 mb-1">
        <span>{t.geboekt}/{t.capaciteit}</span>
        <span className="font-semibold">{t.vulgraad}%</span>
      </div>
      <div className="h-2 rounded-full bg-gray-100 overflow-hidden" role="progressbar" aria-valuenow={t.vulgraad} aria-valuemin={0} aria-valuemax={100}>
        <div className={`h-full rounded-full ${kleur}`} style={{ width: `${Math.min(100, t.vulgraad)}%` }} />
      </div>
    </div>
  );
};

const PresentieModal: React.FC<{ trainingId: number; onClose: () => void; onBroadcast: () => void }> = ({ trainingId, onClose, onBroadcast }) => {
  const { t } = useTranslation('pages');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['opleider-presentie', trainingId], queryFn: () => getPresentie(trainingId) });
  /** Lokale aanvinkingen per registratie-id; ontbreekt = waarde van de server. */
  const [wijzigingen, setWijzigingen] = useState<Record<number, boolean>>({});
  const [melding, setMelding] = useState<{ soort: 'ok' | 'fout'; tekst: string } | null>(null);

  const training = data?.training;
  const lesNogBezig = !!training?.laatste_les_eind && new Date(training.laatste_les_eind).getTime() > Date.now();
  const bevestigbaar = (dl: Deelnemer) => dl.status !== 'geannuleerd';
  const aanwezigVan = (dl: Deelnemer) => wijzigingen[dl.id] ?? dl.aanwezig ?? false;
  const gewijzigd = (data?.deelnemers ?? []).filter((dl) => bevestigbaar(dl) && dl.id in wijzigingen && wijzigingen[dl.id] !== (dl.aanwezig ?? null));
  const bevestigbareRijen = (data?.deelnemers ?? []).filter(bevestigbaar);
  const allesAan = bevestigbareRijen.length > 0 && bevestigbareRijen.every(aanwezigVan);

  const zetAlles = (aan: boolean) => setWijzigingen((w) => ({ ...w, ...Object.fromEntries(bevestigbareRijen.map((dl) => [dl.id, aan])) }));

  const bevestig = useMutation({
    mutationFn: () => bevestigPresentie(trainingId, gewijzigd.map((dl) => ({ id: dl.id, aanwezig: aanwezigVan(dl) }))),
    onMutate: () => setMelding(null),
    onSuccess: async (r) => {
      setMelding({ soort: 'ok', tekst: t('opleiderCockpit.roster.presentieResultaat', { bijgewerkt: r.bijgewerkt, pe: r.pe_geboekt, vervallen: r.vervallen }) });
      await queryClient.invalidateQueries({ queryKey: ['opleider-presentie', trainingId] });
      setWijzigingen({});
    },
    onError: (e: unknown) =>
      setMelding({ soort: 'fout', tekst: (e as { response?: { data?: { message?: string } } })?.response?.data?.message || t('opleiderCockpit.roster.presentieFout') }),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div>
            <h2 className="font-semibold text-gray-900 flex items-center gap-2"><ClipboardList className="w-4 h-4 text-[#0B1F3A]" />{t('opleiderCockpit.roster.presentielijst')}</h2>
            <p className="text-sm text-gray-500">{data?.training.titel}</p>
            {training && (
              <p className="mt-1 inline-flex items-center gap-1 text-xs text-gray-600">
                <Award className="w-3.5 h-3.5 text-[#E5A425]" />
                {training.pe_punten
                  ? t('opleiderCockpit.roster.peInfo', { n: training.pe_punten, register: training.pe_register || t('opleiderCockpit.roster.peRegisterOnbekend') })
                  : t('opleiderCockpit.roster.geenPe')}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {!!data?.deelnemers.length && (
              <button type="button" onClick={onBroadcast} className="inline-flex items-center gap-1.5 rounded-lg bg-[#0B1F3A] px-3 py-2 text-xs font-medium text-white hover:bg-[#1A3459]">
                <Megaphone className="w-4 h-4" />{t('opleiderCockpit.roster.broadcast')}
              </button>
            )}
            <button type="button" onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100" aria-label={t('opleiderCockpit.sluiten')}><X className="w-5 h-5" /></button>
          </div>
        </div>
        <div className="overflow-auto">
          {isLoading ? (
            <div className="p-8 text-center"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#0B1F3A] mx-auto" /></div>
          ) : !data || data.deelnemers.length === 0 ? (
            <p className="p-8 text-center text-gray-500">{t('opleiderCockpit.roster.geenDeelnemers')}</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-5 py-3">#</th>
                  <th className="px-3 py-3">
                    <label className="inline-flex items-center gap-1.5 whitespace-nowrap">
                      <input type="checkbox" checked={allesAan} disabled={lesNogBezig || bevestigbareRijen.length === 0} onChange={(e) => zetAlles(e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-[#0B1F3A] focus:ring-[#0B1F3A]/30" aria-label={t('opleiderCockpit.roster.alleAanwezig')} />
                      {t('opleiderCockpit.roster.aanwezig')}
                    </label>
                  </th>
                  <th className="px-5 py-3">{t('opleiderCockpit.roster.naam')}</th>
                  <th className="px-5 py-3">{t('opleiderCockpit.roster.contact')}</th>
                  <th className="px-5 py-3">{t('opleiderCockpit.roster.bron')}</th>
                  <th className="px-5 py-3">{t('opleiderCockpit.roster.status')}</th>
                  <th className="px-5 py-3"><span className="sr-only">{t('opleiderCockpit.roster.startChat')}</span></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.deelnemers.map((d, i) => (
                  <tr key={d.id}>
                    <td className="px-5 py-3 text-gray-400">{i + 1}</td>
                    <td className="px-3 py-3">
                      <input type="checkbox" checked={aanwezigVan(d)} disabled={lesNogBezig || !bevestigbaar(d)}
                        onChange={(e) => { const aan = e.target.checked; setWijzigingen((w) => ({ ...w, [d.id]: aan })); }}
                        className="h-4 w-4 rounded border-gray-300 text-[#0B1F3A] focus:ring-[#0B1F3A]/30"
                        aria-label={t('opleiderCockpit.roster.aanwezigVoor', { naam: d.naam })} />
                      {d.aanwezigheid_bevestigd_op && (
                        <span className="mt-1 block text-[10px] text-gray-400 whitespace-nowrap">
                          {t('opleiderCockpit.roster.bevestigdOp', { datum: new Date(d.aanwezigheid_bevestigd_op).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' }) })}
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-3 font-medium text-gray-900">
                      {d.naam}{d.registratienummer && <span className="block text-xs text-gray-400">{d.registratienummer}</span>}
                      {d.pe_punten != null && d.pe_punten > 0 && (
                        <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-[#E5A425]/15 px-2 py-0.5 text-[11px] font-medium text-[#8a5d06]">
                          <Award className="w-3 h-3" />{t('opleiderCockpit.roster.peGeboekt', { n: d.pe_punten })}
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-gray-600">
                      {d.email && <a href={`mailto:${d.email}`} className="flex items-center gap-1 hover:text-[#0B1F3A]"><Mail className="w-3.5 h-3.5" />{d.email}</a>}
                      {d.telefoon && <span className="flex items-center gap-1 text-xs text-gray-500"><Phone className="w-3 h-3" />{d.telefoon}</span>}
                    </td>
                    <td className="px-5 py-3 text-gray-600">{t(`opleiderCockpit.signals.bron.${d.bron}`)}</td>
                    <td className="px-5 py-3"><span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{t(`opleider.status${d.status.charAt(0).toUpperCase()}${d.status.slice(1)}`, { defaultValue: d.status })}</span></td>
                    <td className="px-5 py-3 text-right">
                      <button type="button" onClick={() => navigate(`/opleider/chats/cursisten?contact=${d.user_id}`)}
                        className="inline-flex items-center gap-1 whitespace-nowrap rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs font-medium text-[#0B1F3A] hover:bg-blue-50">
                        <MessageCircle className="w-3.5 h-3.5" />{t('opleiderCockpit.roster.startChat')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {!!data?.deelnemers.length && (
          <div className="border-t border-gray-100 p-5 space-y-3">
            <p className="flex items-start gap-2 text-xs text-gray-600">
              <Info className="w-4 h-4 flex-shrink-0 text-[#0B1F3A]" />
              <span>{t('opleiderCockpit.roster.presentieUitleg')}</span>
            </p>
            {lesNogBezig && training?.laatste_les_eind && (
              <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                {t('opleiderCockpit.roster.presentieTeVroeg', { datum: new Date(training.laatste_les_eind).toLocaleString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }) })}
              </p>
            )}
            {melding && (
              <p className={`rounded-lg px-3 py-2 text-xs ${melding.soort === 'ok' ? 'border border-green-200 bg-green-50 text-green-800' : 'border border-red-200 bg-red-50 text-red-700'}`}
                role={melding.soort === 'ok' ? 'status' : 'alert'}>
                {melding.tekst}
              </p>
            )}
            <div className="flex items-center justify-between gap-3">
              <span className="text-xs text-gray-500">{t('opleiderCockpit.roster.wijzigingen', { count: gewijzigd.length })}</span>
              <button type="button" onClick={() => bevestig.mutate()} disabled={lesNogBezig || gewijzigd.length === 0 || bevestig.isPending}
                className="inline-flex items-center gap-1.5 rounded-lg bg-[#0B1F3A] px-4 py-2 text-sm font-medium text-white hover:bg-[#1A3459] disabled:opacity-40">
                {bevestig.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                {t('opleiderCockpit.roster.presentieBevestigen')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const sessieLabel = (s: PresentieSessie) =>
  [s.naam, s.start_at ? new Date(s.start_at).toLocaleString('nl-NL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : null].filter(Boolean).join(' · ') || `#${s.id}`;

/** Bulk-Broadcast: één bericht, als los bericht in de 1-op-1 thread van iedere cursist. */
const BroadcastModal: React.FC<{ trainingId: number; onClose: () => void }> = ({ trainingId, onClose }) => {
  const { t } = useTranslation('pages');
  const navigate = useNavigate();
  const { data } = useQuery({ queryKey: ['opleider-presentie', trainingId], queryFn: () => getPresentie(trainingId) });
  const [bericht, setBericht] = useState('');
  const [sessieId, setSessieId] = useState<number | null>(null);
  const actief = (data?.deelnemers ?? []).filter((d) => d.status !== 'geannuleerd');
  const ontvangers = sessieId ? actief.filter((d) => d.sessie_id === sessieId).length : actief.length;
  const verstuur = useMutation({ mutationFn: () => stuurBroadcast(trainingId, bericht.trim(), sessieId) });
  const foutTekst = (verstuur.error as { response?: { data?: { message?: string } } } | null)?.response?.data?.message;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="broadcast-titel">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div>
            <h2 id="broadcast-titel" className="font-semibold text-gray-900 flex items-center gap-2"><Megaphone className="w-4 h-4 text-[#0B1F3A]" />{t('opleiderCockpit.roster.broadcastTitel')}</h2>
            <p className="text-sm text-gray-500">{data?.training.titel}</p>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100" aria-label={t('opleiderCockpit.sluiten')}><X className="w-5 h-5" /></button>
        </div>
        {verstuur.isSuccess ? (
          <div className="p-6 text-center">
            <CheckCircle2 className="w-10 h-10 text-green-500 mx-auto mb-3" />
            <p className="text-sm font-medium text-gray-900">{t('opleiderCockpit.roster.broadcastVerzonden', { count: verstuur.data.verzonden })}</p>
            <div className="mt-5 flex justify-center gap-2">
              <button type="button" onClick={onClose} className="rounded-lg border border-gray-200 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">{t('opleiderCockpit.sluiten')}</button>
              <button type="button" onClick={() => navigate('/opleider/chats/cursisten')} className="rounded-lg bg-[#0B1F3A] px-4 py-2 text-sm font-medium text-white hover:bg-[#1A3459]">{t('opleiderCockpit.roster.naarChats')}</button>
            </div>
          </div>
        ) : (
          <form className="p-5 space-y-4" onSubmit={(e) => { e.preventDefault(); if (bericht.trim() && ontvangers > 0) verstuur.mutate(); }}>
            <p className="text-sm text-gray-600">{t('opleiderCockpit.roster.broadcastIntro')}</p>
            {!!data?.sessies.length && (
              <label className="block">
                <span className="text-xs font-medium text-gray-700">{t('opleiderCockpit.roster.sessie')}</span>
                <select value={sessieId ?? ''} onChange={(e) => setSessieId(e.target.value ? Number(e.target.value) : null)}
                  className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]/20">
                  <option value="">{t('opleiderCockpit.roster.alleSessies')}</option>
                  {data.sessies.map((s) => <option key={s.id} value={s.id}>{sessieLabel(s)}</option>)}
                </select>
              </label>
            )}
            <label className="block">
              <span className="text-xs font-medium text-gray-700">{t('opleiderCockpit.roster.bericht')}</span>
              <textarea value={bericht} onChange={(e) => setBericht(e.target.value)} rows={5} maxLength={4000} required
                placeholder={t('opleiderCockpit.roster.broadcastPlaceholder')}
                className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]/20" />
            </label>
            {foutTekst && <p className="text-sm text-red-600" role="alert">{foutTekst}</p>}
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500">{t('opleiderCockpit.roster.ontvangers', { count: ontvangers })}</span>
              <button type="submit" disabled={!bericht.trim() || ontvangers === 0 || verstuur.isPending}
                className="inline-flex items-center gap-1.5 rounded-lg bg-[#0B1F3A] px-4 py-2 text-sm font-medium text-white hover:bg-[#1A3459] disabled:opacity-40">
                <Megaphone className="w-4 h-4" />{t('opleiderCockpit.roster.broadcastVerstuur')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export const OpleiderTrainingenOverzichtPage: React.FC = () => {
  const { t } = useTranslation('pages');
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const rol = useOpleiderRol();
  const magBewerken = rol === 'OWNER' || rol === 'ADMINISTRATOR';
  const highlight = params.get('training');
  const [presentie, setPresentie] = useState<number | null>(null);
  const [broadcast, setBroadcast] = useState<number | null>(null);
  const [fout, setFout] = useState<string | null>(null);

  const { data, isLoading } = useQuery({ queryKey: ['opleider-roster'], queryFn: getRoster, refetchInterval: 60_000 });
  const groepschat = useMutation({
    mutationFn: startGroepschat,
    onSuccess: (r) => navigate(`/opleider/chats/cursisten?conv=${r.conversation.id}`),
    onError: (e: unknown) => setFout((e as { response?: { data?: { message?: string } } })?.response?.data?.message || t('opleiderCockpit.roster.groepschatFout')),
  });

  const trainingen = data?.data ?? [];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">{t('opleiderCockpit.roster.intro')}</p>
        {magBewerken && (
          <Link to="/opleider/academy/cursus-invoeren" className="inline-flex items-center gap-2 bg-[#0B1F3A] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#1A3459]">
            <Plus className="w-4 h-4" />{t('opleider.addTraining')}
          </Link>
        )}
      </div>
      {fout && <p className="mb-4 text-sm text-red-600" role="alert">{fout}</p>}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-x-auto">
        {isLoading ? (
          <div className="p-8 text-center"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#0B1F3A] mx-auto" /></div>
        ) : trainingen.length === 0 ? (
          <div className="p-10 text-center">
            <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">{rol === 'TRAINER' ? t('opleiderCockpit.roster.geenKlassen') : t('opleider.noTrainingsYet')}</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
              <tr>
                <th className="px-5 py-3 w-[40%]">{t('opleiderCockpit.roster.training')}</th>
                <th className="px-5 py-3">{t('opleiderCockpit.roster.markten')}</th>
                <th className="px-5 py-3">{t('opleiderCockpit.roster.aanmeldingen')}</th>
                <th className="px-5 py-3">{t('opleiderCockpit.roster.vulgraad')}</th>
                <th className="px-5 py-3">{t('opleiderCockpit.roster.status')}</th>
                <th className="px-5 py-3 text-right">{t('opleiderCockpit.roster.acties')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {trainingen.map((tr) => (
                <tr key={tr.id} className={String(tr.id) === highlight ? 'bg-amber-50/60' : 'hover:bg-gray-50'}>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      {tr.omslagfoto_url ? (
                        <img src={tr.omslagfoto_url} alt="" className="w-14 h-8 rounded object-cover flex-shrink-0" />
                      ) : (
                        <div className="w-14 h-8 rounded bg-[#0B1F3A]/10 flex items-center justify-center flex-shrink-0"><BookOpen className="w-4 h-4 text-[#0B1F3A]/40" /></div>
                      )}
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 truncate max-w-[13rem]">{tr.titel}</p>
                        <p className="text-xs text-gray-500 truncate max-w-[13rem]">{tr.soort_opleiding}{tr.wanneer ? ` · ${tr.wanneer}` : ''}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex gap-1 flex-wrap">
                      {tr.markten.medewerkers && <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">{t('opleiderCockpit.markt.medewerkers')}</span>}
                      {tr.markten.professionals && <span className="text-[11px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">{t('opleiderCockpit.markt.professionals')}</span>}
                      {tr.markten.bedrijven && <span className="text-[11px] px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700">{t('opleiderCockpit.markt.bedrijven')}</span>}
                    </div>
                  </td>
                  <td className="px-5 py-3 text-gray-700">
                    <span className="inline-flex items-center gap-1"><Users className="w-3.5 h-3.5 text-gray-400" />{tr.aanmeldingen}</span>
                    {tr.nieuw > 0 && <span className="ml-2 text-xs font-medium text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded-full">+{tr.nieuw}</span>}
                  </td>
                  <td className="px-5 py-3"><Vulgraad t={tr} /></td>
                  <td className="px-5 py-3 whitespace-nowrap">
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${tr.is_published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {tr.is_published ? t('opleider.published') : t('opleider.concept')}
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button type="button" onClick={() => setPresentie(tr.id)} title={t('opleiderCockpit.roster.presentielijst')} className="p-2 rounded-lg text-gray-500 hover:text-[#0B1F3A] hover:bg-gray-100"><ClipboardList className="w-4 h-4" /></button>
                      <button type="button" onClick={() => setBroadcast(tr.id)} disabled={tr.aanmeldingen === 0} title={t('opleiderCockpit.roster.broadcast')} className="p-2 rounded-lg text-gray-500 hover:text-[#0B1F3A] hover:bg-gray-100 disabled:opacity-40"><Megaphone className="w-4 h-4" /></button>
                      <button type="button" onClick={() => { setFout(null); groepschat.mutate(tr.id); }} disabled={groepschat.isPending || tr.aanmeldingen === 0} title={t('opleiderCockpit.roster.groepschat')} className="p-2 rounded-lg text-gray-500 hover:text-[#0B1F3A] hover:bg-gray-100 disabled:opacity-40"><MessagesSquare className="w-4 h-4" /></button>
                      {magBewerken && (
                        <Link to={`/opleider/trainings/${tr.id}/bewerken`} title={t('opleider.edit')} className="p-2 rounded-lg text-gray-500 hover:text-[#0B1F3A] hover:bg-gray-100"><Edit className="w-4 h-4" /></Link>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {presentie !== null && <PresentieModal trainingId={presentie} onClose={() => setPresentie(null)} onBroadcast={() => { setBroadcast(presentie); setPresentie(null); }} />}
      {broadcast !== null && <BroadcastModal trainingId={broadcast} onClose={() => setBroadcast(null)} />}
    </div>
  );
};

export default OpleiderTrainingenOverzichtPage;
